import React, { useState, useContext, FC } from "react";
import {
  HeaderHeightSetContext,
  HeaderHeightStateContext,
} from "../../../context/headerHeightContext";
import { Link } from "gatsby";
import { WindowLocation } from "@reach/router";
import ContactList from "./ContactListHeader";
import MainNavigation from "./MainNavigation";
import useDimensions from "react-cool-dimensions";
import { cn } from "@/lib/utils";
import { StaticImage } from "gatsby-plugin-image";
import LogoMain from "../../../images/logo-main-compressed.svg";
import Container from "@/components/ui/container";
import loadable from "@loadable/component";
const ClerkHeaderControls = loadable(() => import("./ClerkHeaderControls"), { ssr: false });

interface HeaderProps {
  contactInfo: {
    email: string;
    phone: string;
    phoneText: string;
    workingTime: string;
    facebookLink: string;
  };
  logoOld: string;
  location: WindowLocation;
  scrolled: boolean;
}

const Header: FC<HeaderProps> = ({
  contactInfo,
  location,
  scrolled,
}) => {
  const { observe } = useDimensions({
    useBorderBoxSize: true, // Tell the hook to measure based on the border-box size, default is false
    onResize: ({ height, observe, unobserve }) => {
      if (headerHeight > height && scrolled) return;
      setHeaderHeight(Math.round(height) || 0);
      unobserve();
      observe();
    },
  });

  const [mobileMenuOpen, setMobileMenuOpen] = useState<boolean>(false);
  const [copied, setCopied] = useState(false);
  const setHeaderHeight = useContext(HeaderHeightSetContext);
  const headerHeight = useContext(HeaderHeightStateContext);

  const handleMobileMenuOpenChange = (
    open: boolean
  ) => {
    setMobileMenuOpen(open);
  };

  const handleMobileMenuClose = () => {
    setMobileMenuOpen(false);
  };

  const handleCopyEmail = async () => {
    const mail = contactInfo?.email;
    if (!mail || !navigator?.clipboard?.writeText) return;
    try {
      await navigator.clipboard.writeText(mail);
      setCopied(true);
    } catch {
      // ignore copy errors
    }
  };

  const handleDialogOpenChange = (open: boolean) => {
    if (!open) {
      setCopied(false);
    }
  };

  return (
    <header
      ref={observe}
      className={cn('relative top-0 z-20 transition-opacity', {
        'fixed w-[100svw]': scrolled,
      })}
    >
      <div
        className={cn(
          'py-2 min-h-12 px-7.5 max-800:px-5 flex flex-wrap items-center justify-end bg-white',
          {
            'max-[360px]:px-3': scrolled,
          }
        )}
      >
        <ClerkHeaderControls 
          contactInfo={contactInfo}
          copied={copied} 
          onDialogOpenChange={handleDialogOpenChange}
          onCopyEmail={handleCopyEmail} 
        />
      </div>
      <div
        className={cn(
          'px-7.5 max-800:px-5 py-7.5 pt-11 max-800:py-3 bg-greenDark',
          {
            'py-2 max-[360px]:px-3': scrolled,
            'opacity-80': scrolled && !mobileMenuOpen,
          }
        )}
      >
        <Container className="mt-2 px-0 mui-md:px-4">
          <div className="grid grid-cols-[auto_auto] mui-md:grid-cols-[1fr_5fr] items-center justify-between gap-4">
            <div className="mui-sm:shrink-0">
              <Link
                to="/"
                className={cn('group block relative w-[114px] h-[61px]', {
                  'max-desktop:w-[85px] max-desktop:h-[45px]': scrolled,
                })}
              >
                <StaticImage
                  loading="eager"
                  src="../../../images/logo-old.png"
                  placeholder="dominantColor"
                  layout="constrained"
                  alt="farmeqplus logo 1"
                  width={114}
                  className={cn(
                    [
                      'w-[114px] h-auto block absolute top-1/2 transform -translate-y-1/2 left-0 opacity-100 transition-all duration-500',
                      'max-mui-md:transition-none mui-md:opacity-0 mui-md:-translate-y-1/2 mui-md:scale-0',
                      'mui-md:group-hover:opacity-100 mui-md:group-hover:-translate-y-1/2 mui-md:group-hover:scale-100 mui-md:group-hover:rotate-[360deg]',
                    ],
                    {
                      'max-desktop:w-[85px]': scrolled,
                    }
                  )}
                />
                <LogoMain
                  alt="farmeqplus logo 2"
                  width={114}
                  className={cn(
                    [
                      'w-[114px] h-auto block absolute top-1/2 -translate-y-1/2 left-0 opacity-0 transition-all duration-500',
                      'max-mui-md:transition-none mui-md:opacity-100 mui-md:-translate-y-1/2 mui-md:scale-[1.4]',
                      'mui-md:group-hover:opacity-0',
                    ],
                    {
                      'max-desktop:w-[85px]': scrolled,
                    }
                  )}
                />
              </Link>
            </div>
            <div className="grow">
              <div className="grid max-desktop:grid-cols-[1fr_auto] items-center gap-2">
                <ContactList
                  contactInfo={contactInfo}
                  variant="horizontal"
                  className={cn(!scrolled && 'max-portrait-mode:hidden')}
                />
                <div className="desktop:hidden w-full">
                  <MainNavigation
                    location={location}
                    variant="menu"
                    mobileMenuOpen={mobileMenuOpen}
                    onMobileMenuOpenChange={handleMobileMenuOpenChange}
                    onMobileMenuClose={handleMobileMenuClose}
                  />
                </div>
                <div className="max-[959px]:hidden">
                  <MainNavigation location={location} variant="list" />
                </div>
              </div>
            </div>
          </div>
          <ContactList
            contactInfo={contactInfo}
            variant="vertical"
            className={cn('portrait-mode:hidden', scrolled && 'hidden')}
          />
        </Container>
      </div>
    </header>
  );
};

export default Header;
